import {
  Button,
  Col,
  Drawer,
  Dropdown,
  Input,
  InputRef,
  Menu,
  message,
  notification,
  Row,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/lib/table";
import { saveAs } from "file-saver";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import api from "@/v1/api";
import { ChangeLog } from "@/v1/api/graphql";
import { DISPLAY_DATE_FORMAT } from "@/v1/constants";
import {
  constructChangeLogTexts,
  formatDateTime,
} from "@/v1/components/common/ActionLogDrawer/helpers";
import {
  ActionBlock,
  DateBlock,
  OneLine,
} from "@/v1/components/common/ActionLogDrawer/styledComponents";
import { ContentWrapper } from "@/v1/components/common/styledComponents";
import { formatDate } from "@/v1/components/pages/TaxDeclaration/utils";
import {
  ClickableTd,
  StyledTable,
} from "@/v1/components/pages/VatView/styledComponents";
import DownloadIcon from "@/v1/svgs/download.svg?react";
import type { IUser } from "@/v1/types";
import useEmailParam from "../../v1/components/hooks/useEmailParam";
import useSearchHotkey from "../../v1/components/hooks/useSearchHotkey";
import { cleanSearchInput } from "@/lib/customerSearch/cleanSearchInput";
import { resolveCustomer } from "@/lib/customerSearch/resolveCustomer";
import { taxRegistrationApi } from "./api";
import { getTaxRegistrationFileName } from "./fileNames";
import { getFseFormKind } from "./fseFormByCompanyType";
import { TaxRegistrationDocumentPreview } from "./TaxRegistrationDocumentPreview";
import { TaxRegistrationForm } from "./TaxRegistrationForm";
import { TaxRegistrationKapgForm } from "./TaxRegistrationKapgForm";
import { UnsupportedRechtsformHint } from "./UnsupportedRechtsformHint";
import {
  ExpandedRowHeader,
  PageHeader,
  SubmittedDocumentsContainer,
  TaxRegistrationTableWrapper,
  Title,
} from "./styles";
import {
  isTaxRegistrationReadOnly,
  TaxRegistration,
  TaxRegistrationSubmittedDocument,
  TaxRegistrationStatus,
} from "./types";

interface TaxRegistrationRow {
  user: IUser;
  taxRegistration?: TaxRegistration;
}

type ListMode = "users" | "taxRegistrations";

interface TaxRegistrationActionLogDrawerProps {
  actionText: string;
  changeLogs?: ChangeLog[];
  title: string;
}

interface TaxRegistrationProcessActionsProps {
  taxRegistration: TaxRegistration;
  saveBeforeProcessing: () => Promise<TaxRegistration>;
  onSubmitted: () => Promise<void>;
}

interface TaxRegistrationSubmittedDocumentsTableProps {
  documents: TaxRegistrationSubmittedDocument[];
  email: string;
}

const { Search } = Input;
const CHANGE_MODIFIERS = ["__added", "__deleted"];
const CHANGE_VALUE_KEYS = ["__old", "__new", "__added", "__deleted"];

const STATUS_COLORS: Record<TaxRegistrationStatus, string> = {
  DRAFT: "default",
  READY: "green",
  TRANSMITTED: "blue",
  COMPLETED: "purple",
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const hasChangeValueKeys = (value: Record<string, unknown>) =>
  CHANGE_VALUE_KEYS.some((key) => key in value);

const getChangeModifier = (key: string) =>
  CHANGE_MODIFIERS.find((modifier) => key.endsWith(modifier));

const joinChangePath = (prefix: string, key: string) =>
  prefix ? `${prefix}.${key}` : key;

const flattenTaxRegistrationChanges = (
  changes: Record<string, unknown>,
  prefix = "",
  inheritedModifier = ""
): Record<string, unknown> =>
  Object.entries(changes).reduce<Record<string, unknown>>(
    (flattenedChanges, [key, value]) => {
      const modifier = getChangeModifier(key);
      const keyWithoutModifier = modifier ? key.replace(modifier, "") : key;
      const changePath = joinChangePath(prefix, keyWithoutModifier);

      if (isRecord(value) && !hasChangeValueKeys(value)) {
        return {
          ...flattenedChanges,
          ...flattenTaxRegistrationChanges(
            value,
            changePath,
            modifier || inheritedModifier
          ),
        };
      }

      return {
        ...flattenedChanges,
        [`${changePath}${modifier || inheritedModifier}`]: value,
      };
    },
    {}
  );

const TaxRegistrationActionLogDrawer = ({
  actionText,
  changeLogs = [],
  title,
}: TaxRegistrationActionLogDrawerProps) => {
  const [shouldShowLog, setShouldShowLog] = useState(false);
  const onClick = useCallback(() => setShouldShowLog(true), []);

  const changeLogList = useMemo(
    () =>
      changeLogs.map(({ changes, changedAt, changedBy, modelName }) => (
        <ActionBlock key={`${changedAt}-${JSON.stringify(changes)}`}>
          <DateBlock>{formatDateTime(changedAt)}</DateBlock>
          {changedBy && <div>{changedBy}</div>}
          {constructChangeLogTexts(
            flattenTaxRegistrationChanges(changes),
            modelName
          ).map((text, idx) => (
            <OneLine key={`${changedAt}-${idx}`}>
              <Tooltip placement="topLeft" title={text}>
                {text}
              </Tooltip>
            </OneLine>
          ))}
        </ActionBlock>
      )),
    [changeLogs]
  );

  return (
    <>
      <Button type="link" onClick={onClick} aria-label={actionText}>
        {actionText}
      </Button>
      <Drawer
        title={title}
        placement="right"
        onClose={() => setShouldShowLog(false)}
        visible={shouldShowLog}
      >
        {changeLogList}
      </Drawer>
    </>
  );
};

const saveXml = (xml: string, fileName: string) => {
  saveAs(new Blob([xml], { type: "application/xml" }), fileName);
};

const saveBase64Pdf = (base64: string, fileName: string) => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  saveAs(new Blob([bytes], { type: "application/pdf" }), fileName);
};

const TaxRegistrationSubmittedDocumentsTable = ({
  documents,
  email,
}: TaxRegistrationSubmittedDocumentsTableProps) => {
  const { t } = useTranslation("TaxRegistration");
  const openPdf = async (declarationId: number) => {
    const openedWindow = window.open();
    try {
      const pdfUrl = await api.kontax.getDeclarationPdf(
        email,
        String(declarationId)
      );
      if (openedWindow) {
        openedWindow.location.href = pdfUrl;
      }
      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 3000);
    } catch {
      openedWindow?.close();
    }
  };

  return (
    <SubmittedDocumentsContainer>
      <h3>{t("documents.title")}</h3>
      <StyledTable>
        <thead>
          <tr>
            <td>{t("documents.timePeriod")}</td>
            <td>{t("documents.uploadedOn")}</td>
            <td>{t("documents.transmission")}</td>
            <td>{t("documents.declaration")}</td>
          </tr>
        </thead>
        <tbody>
          {documents.length === 0 ? (
            <tr>
              <td colSpan={4} style={{ textAlign: "center" }}>
                {t("documents.noDocuments")}
              </td>
            </tr>
          ) : (
            documents.map((document) => (
              <tr key={document.id}>
                <td>
                  {document.period}, {document.year}
                </td>
                <td>
                  {document.uploadedAt || document.createdAt
                    ? moment(document.uploadedAt ?? document.createdAt).format(
                        DISPLAY_DATE_FORMAT
                      )
                    : "-"}
                </td>
                <td>
                  {document.transmissionMethod === "MANUAL"
                    ? t("documents.manual")
                    : t("documents.automatic")}
                  {document.submitterEmail && (
                    <span style={{ color: "#888", marginLeft: 4 }}>
                      ({document.submitterEmail})
                    </span>
                  )}
                </td>
                <ClickableTd onClick={() => openPdf(document.id)}>
                  <DownloadIcon />
                  {` ${t("documents.open")}`}
                </ClickableTd>
              </tr>
            ))
          )}
        </tbody>
      </StyledTable>
    </SubmittedDocumentsContainer>
  );
};

const TaxRegistrationProcessActions = ({
  taxRegistration,
  saveBeforeProcessing,
  onSubmitted,
}: TaxRegistrationProcessActionsProps) => {
  const { t } = useTranslation("TaxRegistration");
  const [currentTaxRegistration, setCurrentTaxRegistration] =
    useState(taxRegistration);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [previewDraftError, setPreviewDraftError] = useState<string>();
  const [loadingAction, setLoadingAction] = useState<
    "downloadXml" | "preview" | "submit" | undefined
  >();
  const isLoading = Boolean(loadingAction);

  /*
   * Named from the record that was just saved, not from the one rendered: both
   * downloads save first. The Mandant's name comes from the master data.
   */
  const buildFileName = useCallback(
    (taxRegistrationToName: TaxRegistration, extension: "xml" | "pdf") =>
      getTaxRegistrationFileName({
        extension,
        user: taxRegistrationToName.user,
        fallback: taxRegistrationToName.id,
      }),
    []
  );

  useEffect(() => {
    setCurrentTaxRegistration(taxRegistration);
  }, [taxRegistration]);

  const saveAndRequireReady = useCallback(async () => {
    const savedTaxRegistration = await saveBeforeProcessing();
    setCurrentTaxRegistration(savedTaxRegistration);
    if (savedTaxRegistration.status !== "READY") {
      throw new Error(String(t("messages.processingRequiresReady")));
    }
    return savedTaxRegistration;
  }, [saveBeforeProcessing, t]);

  const getDraft = useCallback(async (taxRegistrationId: string) => {
    return taxRegistrationApi.getTaxRegistrationDraft(taxRegistrationId);
  }, []);

  /*
   * The draft is built before the modal opens, so the preview answers the same
   * question a transmission would: ERiC validates the payload and rejects it
   * with the reason. Until SMT-1335 only "XML herunterladen" asked for it,
   * which is the one action nobody runs first, so a draft ERiC would refuse
   * looked clean here and failed on transmission instead.
   *
   * A failed draft still opens the modal, with the message above the rows: the
   * rows are how the operator finds the field it is about. Only a failed save,
   * or a record that did not reach READY, keeps the modal shut — there is
   * nothing to preview then.
   *
   * The failure is passed on as it is and never classified here. `BaseApi`
   * turns an ERiC rejection, an expired session, a timeout and a 500 into the
   * same plain `Error`, so there is no discriminator to branch on; the banner
   * is titled neutrally instead of guessing.
   */
  const handlePreview = useCallback(async () => {
    setLoadingAction("preview");
    try {
      const savedTaxRegistration = await saveAndRequireReady();
      let draftError: string | undefined;
      try {
        await getDraft(savedTaxRegistration.id);
      } catch (error) {
        const detail = error instanceof Error ? error.message : String(error);
        draftError = detail || String(t("preview.draftFailed"));
      }
      setPreviewDraftError(draftError);
      setIsPreviewVisible(true);
    } catch (error) {
      const detail = error instanceof Error ? error.message : "";
      notification.error({
        message: t("messages.previewError", { error: detail }),
      });
    } finally {
      setLoadingAction(undefined);
    }
  }, [getDraft, saveAndRequireReady, t]);

  const handleDownloadXml = useCallback(async () => {
    setLoadingAction("downloadXml");
    try {
      const savedTaxRegistration = await saveAndRequireReady();
      const result = await getDraft(savedTaxRegistration.id);
      saveXml(result.requestXml, buildFileName(savedTaxRegistration, "xml"));
    } catch (error) {
      const detail = error instanceof Error ? error.message : "";
      notification.error({
        message: t("messages.downloadXmlError", { error: detail }),
      });
    } finally {
      setLoadingAction(undefined);
    }
  }, [buildFileName, getDraft, saveAndRequireReady, t]);

  const handleSubmit = useCallback(async () => {
    setLoadingAction("submit");
    try {
      const savedTaxRegistration = await saveAndRequireReady();
      const result = await taxRegistrationApi.submitTaxRegistration(
        savedTaxRegistration.id
      );
      saveBase64Pdf(result.pdf, buildFileName(savedTaxRegistration, "pdf"));
      message.success(t("messages.submitted"));
      await onSubmitted();
    } catch (error) {
      const detail = error instanceof Error ? error.message : "";
      notification.error({
        message: t("messages.submitError", { error: detail }),
      });
    } finally {
      setLoadingAction(undefined);
    }
  }, [buildFileName, onSubmitted, saveAndRequireReady, t]);

  const actionMenu = useMemo(
    () => (
      <Menu>
        <Menu.Item
          disabled={isLoading}
          key="DOWNLOAD_XML"
          onClick={handleDownloadXml}
        >
          {t("actions.downloadXml")}
        </Menu.Item>
        <Menu.Item disabled={isLoading} key="SUBMIT" onClick={handleSubmit}>
          {loadingAction === "submit" ? (
            <>
              <Spin size="small" /> {t("actions.sendingViaEric")}
            </>
          ) : (
            t("actions.sendViaEric")
          )}
        </Menu.Item>
      </Menu>
    ),
    [handleDownloadXml, handleSubmit, isLoading, loadingAction, t]
  );

  return (
    <span onClick={(event) => event.stopPropagation()}>
      <Dropdown.Button
        disabled={isLoading}
        onClick={handlePreview}
        overlay={actionMenu}
      >
        {loadingAction === "submit"
          ? t("actions.sendingViaEricEllipsis")
          : t("actions.preview")}
      </Dropdown.Button>
      <TaxRegistrationDocumentPreview
        draftError={previewDraftError}
        taxRegistration={currentTaxRegistration}
        onCancel={() => setIsPreviewVisible(false)}
        visible={isPreviewVisible}
      />
    </span>
  );
};

export const TaxRegistrationView = () => {
  const { t } = useTranslation("TaxRegistration");
  const [emailParam, setEmailParam] = useEmailParam();
  const [emailInput, setEmailInput] = useState(emailParam || "");
  const [emailFilter, setEmailFilter] = useState<string | undefined>(
    emailParam || undefined
  );
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
  const [rows, setRows] = useState<TaxRegistrationRow[]>([]);
  const [hasRequestedUsers, setHasRequestedUsers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [listMode, setListMode] = useState<ListMode>("users");
  const [statusFilter, setStatusFilter] = useState<
    TaxRegistrationStatus | undefined
  >(undefined);
  const searchRef = useRef<InputRef>(null);
  const previousEmailParam = useRef<string | null | undefined>(undefined);
  const skipNextEmailParamLoad = useRef(false);
  // Only the newest filter action may apply: an older customer search that
  // answers late must not restore the customer it found.
  const lastFilterRequestId = useRef(0);

  useSearchHotkey(searchRef);

  const fetchUsers = useCallback(
    async (search: string | undefined, page: number, size: number) => {
      setHasRequestedUsers(true);
      setLoading(true);
      try {
        const { users, count } = await api.kontax.getUsers({
          search,
          page,
          pageSize: size,
        });
        const taxRegistrationsResponse = users.length
          ? await taxRegistrationApi.getTaxRegistrations(
              undefined,
              users.map((user) => user.id)
            )
          : { count: 0, taxRegistrations: [] };
        const taxRegistrationByUserId = new Map(
          taxRegistrationsResponse.taxRegistrations.map((taxRegistration) => [
            taxRegistration.userId,
            taxRegistration,
          ])
        );

        setRows(
          users.map((user) => ({
            user,
            taxRegistration: taxRegistrationByUserId.get(user.id),
          }))
        );
        setTotal(count);
        setCurrentPage(page);
        setPageSize(size);
      } catch (error) {
        const detail = error instanceof Error ? error.message : "";
        message.error(t("messages.loadError", { error: detail }));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  const fetchTaxRegistrations = useCallback(
    async (
      page: number,
      size: number,
      email?: string,
      status?: TaxRegistrationStatus
    ) => {
      setHasRequestedUsers(true);
      setLoading(true);
      try {
        const { count, taxRegistrations } =
          await taxRegistrationApi.getTaxRegistrations(email, undefined, {
            limit: size,
            offset: (page - 1) * size,
            status,
            withTaxRegistration: true,
          });

        setRows(
          taxRegistrations.map((taxRegistration) => ({
            user: taxRegistration.user,
            taxRegistration,
          }))
        );
        setTotal(count);
        setCurrentPage(page);
        setPageSize(size);
      } catch (error) {
        const detail = error instanceof Error ? error.message : "";
        message.error(t("messages.loadError", { error: detail }));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    if (skipNextEmailParamLoad.current) {
      skipNextEmailParamLoad.current = false;
      previousEmailParam.current = emailParam;
      return;
    }

    if (previousEmailParam.current === emailParam) return;

    previousEmailParam.current = emailParam;
    if (emailParam) {
      setEmailInput(emailParam);
      setEmailFilter(emailParam);
      setExpandedRowKeys([]);
      setListMode("users");
      fetchUsers(emailParam, 1, pageSize);
    }
  }, [emailParam, fetchUsers, pageSize]);

  const loadUsers = (
    search = emailFilter,
    page = currentPage,
    size = pageSize
  ) => fetchUsers(search, page, size);

  // The list keeps filtering by what was typed, but only a unique match is
  // selected, and by its real email: the `email` param travels to views whose
  // calls cannot use a Public ID (SMT-1396).
  const applyFilter = async (value = emailInput) => {
    const requestId = ++lastFilterRequestId.current;
    const search = cleanSearchInput(value) || undefined;
    const resolution = search
      ? await resolveCustomer(search).catch(() => undefined)
      : undefined;
    if (requestId !== lastFilterRequestId.current) return;
    const email =
      resolution?.kind === "one" ? resolution.user.email : undefined;
    const filter = email || search;
    previousEmailParam.current = email;
    setEmailParam(email);
    setEmailInput(filter || "");
    setEmailFilter(filter);
    setStatusFilter(undefined);
    setExpandedRowKeys([]);
    setListMode("users");
    loadUsers(filter, 1, pageSize);
  };

  const loadAll = () => {
    lastFilterRequestId.current++;
    setEmailInput("");
    previousEmailParam.current = undefined;
    setEmailParam(undefined);
    setEmailFilter(undefined);
    setStatusFilter(undefined);
    setExpandedRowKeys([]);
    setListMode("taxRegistrations");
    fetchTaxRegistrations(1, pageSize, undefined, undefined);
  };

  const applyStatusFilter = (status?: TaxRegistrationStatus) => {
    lastFilterRequestId.current++;
    setStatusFilter(status);
    setExpandedRowKeys([]);
    setListMode("taxRegistrations");
    fetchTaxRegistrations(1, pageSize, emailFilter, status);
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setExpandedRowKeys([]);
    const nextPage = pagination.current || 1;
    const nextPageSize = pagination.pageSize || pageSize;
    if (listMode === "taxRegistrations") {
      fetchTaxRegistrations(nextPage, nextPageSize, emailFilter, statusFilter);
      return;
    }
    loadUsers(emailFilter, nextPage, nextPageSize);
  };

  const refreshCurrentRows = useCallback(async () => {
    if (listMode === "taxRegistrations") {
      await fetchTaxRegistrations(
        currentPage,
        pageSize,
        emailFilter,
        statusFilter
      );
      return;
    }
    await fetchUsers(emailFilter, currentPage, pageSize);
  }, [
    currentPage,
    emailFilter,
    fetchTaxRegistrations,
    fetchUsers,
    listMode,
    pageSize,
    statusFilter,
  ]);

  const columns: ColumnsType<TaxRegistrationRow> = [
    {
      title: t("columns.name"),
      render: (_, { user }) =>
        user.legalName ||
        [user.firstName, user.lastName].filter(Boolean).join(" ") ||
        "—",
    },
    {
      title: t("columns.email"),
      dataIndex: ["user", "email"],
    },
    {
      title: t("columns.status"),
      render: (_, { taxRegistration }) =>
        taxRegistration ? (
          <Tag color={STATUS_COLORS[taxRegistration.status]}>
            {taxRegistration.status}
          </Tag>
        ) : (
          "—"
        ),
    },
    {
      title: t("columns.updatedAt"),
      render: (_, { taxRegistration }) =>
        taxRegistration ? formatDate(taxRegistration.updatedAt) ?? "—" : "—",
    },
  ];

  return (
    <ContentWrapper>
      <PageHeader>
        <Title>{t("title")}</Title>
      </PageHeader>
      <Row gutter={8} style={{ flex: 1, marginBottom: 16 }}>
        <Col flex="auto" xl={8}>
          <Search
            ref={searchRef}
            allowClear
            data-test="searchBar"
            enterButton={t("actions.search")}
            id="search"
            onChange={(event) => setEmailInput(event.target.value)}
            onKeyPress={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
              }
            }}
            onSearch={applyFilter}
            placeholder={
              t("customerSearch.placeholder", { ns: "translation" }) || ""
            }
            value={emailInput}
          />
        </Col>
        <Col>
          <Select
            allowClear
            onChange={applyStatusFilter}
            options={Object.keys(STATUS_COLORS).map((status) => ({
              label: status,
              value: status,
            }))}
            placeholder={t("filters.status")}
            style={{ width: 180 }}
            value={statusFilter}
          />
        </Col>
        <Col>
          <Button data-test="loadAll" onClick={loadAll} type="default">
            {t("actions.loadAll")}
          </Button>
        </Col>
      </Row>
      {hasRequestedUsers && (
        <TaxRegistrationTableWrapper>
          <Table
            bordered
            columns={columns}
            dataSource={rows}
            expandRowByClick
            expandable={{
              expandedRowKeys,
              expandedRowRender: ({ taxRegistration, user }) => {
                // Both forms take the same props, so the branch below decides
                // only which Datenart the operator gets, never how it is wired.
                const formProps = {
                  onSaved: async () => {
                    if (listMode === "taxRegistrations") {
                      await fetchTaxRegistrations(
                        currentPage,
                        pageSize,
                        emailFilter,
                        statusFilter
                      );
                      return;
                    }
                    await loadUsers(emailFilter, currentPage, pageSize);
                  },
                  readOnly: taxRegistration
                    ? isTaxRegistrationReadOnly(taxRegistration)
                    : false,
                  processActions: ({
                    saveBeforeProcessing,
                  }: {
                    saveBeforeProcessing: () => Promise<TaxRegistration>;
                  }) =>
                    taxRegistration?.status === "READY" ? (
                      <TaxRegistrationProcessActions
                        taxRegistration={taxRegistration}
                        saveBeforeProcessing={saveBeforeProcessing}
                        onSubmitted={refreshCurrentRows}
                      />
                    ) : undefined,
                  taxRegistration,
                  user,
                };
                const formKind = getFseFormKind(user.companyType);

                return (
                  <>
                    <ExpandedRowHeader>
                      <TaxRegistrationActionLogDrawer
                        actionText={t("logs.view")}
                        title={t("logs.title")}
                        changeLogs={taxRegistration?.changeLogs}
                      />
                    </ExpandedRowHeader>
                    {taxRegistration &&
                      ["TRANSMITTED", "COMPLETED"].includes(
                        taxRegistration.status
                      ) && (
                        <TaxRegistrationSubmittedDocumentsTable
                          documents={taxRegistration.submittedDocuments || []}
                          email={taxRegistration.email}
                        />
                      )}
                    {formKind === "EUN" && (
                      <TaxRegistrationForm {...formProps} />
                    )}
                    {formKind === "KAPG" && (
                      <TaxRegistrationKapgForm {...formProps} />
                    )}
                    {!formKind && (
                      <UnsupportedRechtsformHint
                        companyType={user.companyType}
                      />
                    )}
                  </>
                );
              },
              onExpand: (expanded, { user }) => {
                // Picking a row is a newer selection than any pending search.
                lastFilterRequestId.current++;
                setExpandedRowKeys(expanded ? [user.id] : []);
                skipNextEmailParamLoad.current = true;
                previousEmailParam.current = expanded ? user.email : undefined;
                setEmailParam(expanded ? user.email : undefined);
              },
            }}
            loading={loading}
            onChange={handleTableChange}
            pagination={{
              current: currentPage,
              pageSize,
              showSizeChanger: true,
              total,
            }}
            rowKey={({ user }) => user.id}
          />
        </TaxRegistrationTableWrapper>
      )}
    </ContentWrapper>
  );
};
